import type { Command } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { AllSelection, TextSelection, type Transaction } from "@tiptap/pm/state";
import { isEmpty } from "lodash-es";

declare module "@tiptap/core" {
  /* oxlint-disable @typescript-eslint/consistent-type-definitions */
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

export type IndentOptions = {
  types: string[];
  indentInPixels: number;
};

const Indent = Extension.create<IndentOptions>({
  name: "indent",

  addOptions: () => ({
    types: [],
    indentInPixels: 25,
  }),

  addGlobalAttributes() {
    const { options } = this;

    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: null,
            parseHTML: (element) => {
              const { marginLeft } = element.style;
              const result = marginLeft.match(/(?<pixels>\d+)px/);
              const pixels = result?.groups?.pixels;

              if (isEmpty(pixels)) {
                return null;
              }

              const level = Math.floor(Number(pixels) / options.indentInPixels);
              return level > 0 ? level : null;
            },
            renderHTML: (attributes) =>
              attributes?.indent > 0 ? { style: `margin-left: ${options.indentInPixels * attributes.indent}px` } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    const { options } = this;

    const updateIndentLevel = (tr: Transaction, delta: number): Transaction => {
      const { doc, selection } = tr;

      if (selection instanceof TextSelection || selection instanceof AllSelection) {
        const { from, to } = selection;
        doc.nodesBetween(from, to, (node, pos) => {
          if (options.types.includes(node.type.name)) {
            const indent = Math.max(0, (node.attrs.indent ?? 0) + delta);

            if (indent !== node.attrs.indent) {
              tr.setNodeAttribute(pos, "indent", indent);
            }
          }
        });
      }

      return tr;
    };

    const applyIndent: (direction: number) => () => Command =
      (direction) =>
      () =>
      ({ tr, state, dispatch }) => {
        const { selection } = state;
        tr.setSelection(selection);
        updateIndentLevel(tr, direction);

        if (tr.docChanged) {
          dispatch?.(tr);
          return true;
        }

        return false;
      };

    return {
      indent: applyIndent(1),
      outdent: applyIndent(-1),
    };
  },

  addKeyboardShortcuts() {
    const { editor } = this;

    return {
      Tab: () => editor.commands.indent(),
      "Shift-Tab": () => editor.commands.outdent(),
    };
  },
});

export default Indent;
